/**
 * Portal base path helpers.
 *
 * `VITE_PORTAL_BASE_PATH` controls where the portal is mounted:
 * - "/" (default) → portal.sposasuite.com/*
 * - "/portal"     → sposasuite.com/portal/* (transition window)
 *
 * Inside React Router, BrowserRouter's `basename` handles the prefix
 * automatically — so `<Link to="/dashboard">` resolves correctly for both
 * configurations. These helpers exist for the cases that live OUTSIDE the
 * router: `window.location.href`, Stripe return URLs, magic-link emails,
 * service worker scope.
 */

const RAW = (import.meta.env.VITE_PORTAL_BASE_PATH ?? "/").trim();

/** Normalized base path: always starts with `/`, never ends with `/` (except root). */
export const BASE_PATH = (() => {
  if (!RAW || RAW === "/") return "/";
  const withLeading = RAW.startsWith("/") ? RAW : `/${RAW}`;
  return withLeading.endsWith("/") ? withLeading.slice(0, -1) : withLeading;
})();

/** Join the base path with a route path. Always returns a string starting with `/`. */
export function buildPath(path = "/"): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (BASE_PATH === "/") return clean;
  return clean === "/" ? BASE_PATH : `${BASE_PATH}${clean}`;
}

/** Build an absolute URL (origin + base path + route). */
export function buildAbsoluteUrl(path = "/"): string {
  return `${window.location.origin}${buildPath(path)}`;
}
